import { useState } from 'react';
import { FEVER_C, FEVER_WATCH_C } from '@oncocoach/care';
import type { AppSession, GradedSymptom, PatientView, SymptomCode } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { SYMPTOM_LABEL, fmtAgo, fmtTemp, toFahrenheit } from '../../lib/format';
import { Icon } from '../../components/Mark';

/**
 * Logging a temperature.
 *
 * This is the screen the whole product turns on. On active treatment, 38.0 °C means the patient
 * should be ringing the oncology emergency line tonight, and everything here is arranged so that
 * cannot be missed: the number is entered in one field, the emergency panel appears the moment a
 * fever is typed — before anything is submitted — and it does not offer a way to dismiss it and
 * carry on. The entry is still saved, so the care team sees it either way.
 *
 * Fahrenheit is a display: the wire and the record are Celsius, always.
 */
export function TemperatureFlow({ session, view, onDone, onClose }: { session: AppSession; view: PatientView; onDone: () => Promise<void>; onClose: () => void }) {
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState<'C' | 'F'>('C');
  const [symptoms, setSymptoms] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ insight: string | null; celsius: number } | null>(null);

  const raw = Number(value.replace(/,/g, ''));
  const celsius = Number.isFinite(raw) ? (unit === 'F' ? Math.round(((raw - 32) * 5) / 9 * 10) / 10 : Math.round(raw * 10) / 10) : NaN;
  const valid = value.trim() !== '' && Number.isFinite(celsius) && celsius >= 30 && celsius <= 43;
  // Shown while they are still typing. Waiting for a submit would be waiting for no reason.
  const emergency = valid && celsius >= FEVER_C && view.track.feverIsEmergency;
  const watch = valid && celsius >= FEVER_WATCH_C && celsius < FEVER_C;
  const last = view.latestTemperature;

  const offered = view.track.symptoms.slice(0, 6);
  const setGrade = (code: SymptomCode, grade: number) => setSymptoms((s) => ({ ...s, [code]: grade }));

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const graded: GradedSymptom[] = Object.entries(symptoms)
        .filter(([, g]) => g > 0)
        .map(([code, grade]) => ({ code: code as SymptomCode, grade: grade as GradedSymptom['grade'] }));
      const r = await api.postEvent({ type: 'log-temperature', celsius, ...(graded.length > 0 ? { symptoms: graded } : {}), source: 'manual' }, session.token);
      setDone({ insight: r.insight, celsius });
      await onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Log your temperature">
      <div className="modal">
        <div className="modal-head">
          <h2>{done ? 'Logged' : 'Take your temperature'}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        </div>

        {done ? (
          <div className="modal-body">
            <p className="reading-big">
              {fmtTemp(done.celsius)}C <span className="hint">({toFahrenheit(done.celsius)}°F)</span>
            </p>
            {done.insight ? <p className={done.celsius >= FEVER_C && view.track.feverIsEmergency ? 'callout callout-urgent' : 'callout'}>{done.insight}</p> : null}
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <div className="modal-body">
            {last ? (
              <p className="hint">
                Last: {fmtTemp(last.celsius)}C · {fmtAgo(last.at)}
              </p>
            ) : (
              <p className="hint">Under the tongue, at the same time each morning, and not within half an hour of a hot or cold drink.</p>
            )}

            <div className="field-row">
              <label className="field">
                <span>Temperature</span>
                <input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder={unit === 'C' ? '36.8' : '98.2'} aria-label="Temperature" autoFocus />
              </label>
              <div className="unit-toggle" role="group" aria-label="Unit">
                {(['C', 'F'] as const).map((u) => (
                  <button key={u} type="button" className={unit === u ? 'on' : ''} onClick={() => setUnit(u)} aria-pressed={unit === u}>
                    °{u}
                  </button>
                ))}
              </div>
            </div>
            {valid && unit === 'F' ? <p className="hint">That is {fmtTemp(celsius)}C, which is what your team will see.</p> : null}

            {emergency ? (
              <div className="callout callout-urgent" role="alert">
                <strong>{fmtTemp(celsius)}C is a fever, and during treatment that is an emergency.</strong>
                <p>
                  Call {view.practiceName}'s oncology emergency line now, or go to your nearest emergency department. Tell them you are having cancer treatment.
                </p>
                <p>Do not wait to see if it settles, and do not take paracetamol first — it can hide the fever.</p>
                <p className="hint">Log it below as well, so your team can see it. But go now.</p>
              </div>
            ) : null}
            {watch ? (
              <div className="callout" role="status">
                {fmtTemp(celsius)}C is a little raised. Take it again in an hour.{view.track.feverIsEmergency ? ` If it reaches ${FEVER_C.toFixed(1)}°C, ring the emergency line straight away.` : ''}
              </div>
            ) : null}

            <fieldset className="grades">
              <legend>Anything else today?</legend>
              {offered.map((code) => (
                <div key={code} className="grade-row">
                  <span className="grade-name">{SYMPTOM_LABEL[code]}</span>
                  <div className="grade-scale" role="group" aria-label={SYMPTOM_LABEL[code]}>
                    {[0, 1, 2, 3, 4].map((g) => (
                      <button key={g} type="button" className={(symptoms[code] ?? 0) === g ? 'on' : ''} onClick={() => setGrade(code, g)} aria-pressed={(symptoms[code] ?? 0) === g}>
                        {['None', 'Mild', 'Mod', 'Severe', 'Worst'][g]}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </fieldset>

            {error ? <p className="form-error">{error}</p> : null}
            <button type="button" className="btn btn-primary" disabled={!valid || busy} onClick={() => void submit()}>
              {busy ? 'Saving…' : 'Log temperature'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
